import { useState } from 'react';
import {
  facilityGuardianRules,
  facilityGuardianRulesSave,
  facilityVoiceScribe,
  facilityVoiceScribeSave,
  guardianRulesDefault,
  guardianRulesDefaultSave,
  oncallRotationSave,
  oncallStepSave,
  oncallStepTest,
  pulseOncallV3,
  userContactConsent,
  voiceScribeDefault,
  voiceScribeDefaultSave,
  type ApiError,
  type GuardianOwner,
  type GuardianRules,
  type GuardianRuleKind,
  type ScribeNoteType,
  type VoiceAssistant,
  type VoiceScribeSettings,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { ErrLine, I, LOCKED_TAG, maskPhone, sx, useApi, useGuarded, usePage } from './kit';
import { RuleRows, optStyle, type RuleRow } from './RuleRows';
import { FacilityOnCall } from './FacilityOnCall';
import { BuildingLoading, PickOneBuilding, useBuilding } from './building';

const HEAD = (dot: string, text: string) => (
  <div style={sx('display:flex;align-items:center;gap:9px;margin-bottom:4px;')}>
    <span style={sx('width:7px;height:7px;border-radius:50%;background:' + dot + ';flex:0 0 7px;')} />
    <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>{text}</span>
  </div>
);
const PEND = 'font-size:16px;font-weight:700;color:#A5561A;margin-bottom:8px;';

// ── On-call (design seIsOnCall V4 T10440; L24140-24170) ─────────────────────
// Spec V4 step 3: each step says whether its number may be texted and called, and from what
// consent. "Record consent" stores a verbal opt-in with who recorded it and the exact words.

/** The opt-in sentence a verbal consent records (V4 mcConsText, the work variant). */
const CONSENT_WORDS = 'I agree Aithora may send me texts and automated calls about my work and urgent alerts at this number. Message frequency varies. Message and data rates may apply. Reply STOP to opt out, HELP for help.';

const TRIGGER_WORD: Record<string, string> = {
  building_paused_ai: 'A building paused AI', system_pulse_outage: 'System Pulse outage', emergency_unclaimed: 'emergency unclaimed', guard_halted: 'guard halted',
};
const WAITS = [5, 10, 15] as const;

/**
 * On-call for Super Admin and Operator. With nothing picked in the facility switcher: Aithora's
 * own pager (System Pulse and Oversight). With one building picked: that building's on-call
 * schedule, named by its id. With a region or several buildings: pick one first.
 */
export function OnCallSection() {
  const building = useBuilding();
  if (building.mode === 'loading') return <BuildingLoading />;
  if (building.mode === 'narrowed') return <PickOneBuilding what="On-call" />;
  if (building.mode === 'picked') {
    return (
      <div data-screen-label="On-call" data-testid="settings-oncall-building" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 2px;max-width:980px;')}>
        {HEAD('#2F5C97', 'On-call · ' + building.name)}
        <FacilityOnCall key={building.orgId} orgId={building.orgId} />
      </div>
    );
  }
  return <TeamOnCall />;
}

function TeamOnCall() {
  const { client } = useSession();
  const { changed } = usePage();
  const { roReason, wr } = useGuarded();
  const q = useApi((c, s) => pulseOncallV3(c, { signal: s }), []);
  const [tested, setTested] = useState<Record<number, string>>({});
  const [recorded, setRecorded] = useState<Record<number, boolean>>({});
  const [err, setErr] = useState<ApiError | null>(null);
  const run = async (fn: () => Promise<void>) => {
    setErr(null);
    try {
      await fn();
      changed();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  if (!q.data && !q.error) return <Skeleton rows={6} label="Loading on-call" />;
  const d = q.data;
  const steps = d?.steps ?? [];
  const rot = (d?.rotation ?? null) as { members?: string[]; cadence?: 'daily' | 'weekly' | null; handoff?: 'mon_8am' | 'fri_5pm' | null; timezone?: string } | null;
  const setRot = (patch: { cadence?: 'daily' | 'weekly'; handoff?: 'mon_8am' | 'fri_5pm' }) => void run(async () => {
    await oncallRotationSave(client, { body: { members: rot?.members ?? [], cadence: patch.cadence ?? rot?.cadence ?? 'weekly', handoff: patch.handoff ?? rot?.handoff ?? 'mon_8am', ...(rot?.timezone ? { timezone: rot.timezone } : {}) } });
    q.reload();
  });
  const triggers = (d?.routed_triggers ?? []).map((t) => TRIGGER_WORD[t] ?? t.replace(/_/g, ' '));
  const rows: RuleRow[] = [
    { label: 'Rotation', sub: 'Who is primary moves on this schedule.', opts: (['Daily', 'Weekly'] as const).map((o) => ({ label: o, on: (rot?.cadence ?? null) === o.toLowerCase(), onClick: () => setRot({ cadence: o.toLowerCase() as 'daily' | 'weekly' }) })) },
    { label: 'Hand-off time', sub: 'The outgoing person gets a summary of anything still open.', opts: ([['Mon 8am', 'mon_8am'], ['Fri 5pm', 'fri_5pm']] as const).map(([l, k]) => ({ label: l, on: rot?.handoff === k, onClick: () => setRot({ handoff: k }) })) },
    { label: 'How each step is reached', sub: 'Push first, then SMS, then a phone call through Twilio.', opts: [], ...LOCKED_TAG },
    { label: 'What pages on-call', sub: (triggers.length ? triggers.join(' · ') : 'Nothing is routed here yet') + '. Nothing else wakes anyone.', opts: [], ...LOCKED_TAG },
  ];
  return (
    <div data-screen-label="On-call" data-testid="settings-oncall" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 2px;max-width:980px;')}>
      {HEAD('#2F5C97', 'On-call · who gets paged')}
      <div style={sx('font-size:16px;color:#54606C;margin-bottom:12px;text-wrap:pretty;')}>{I('System Pulse and Oversight send their pages here. Every page, answer and hand-off is sealed to the Truth Ledger.')}</div>
      {q.error && <ErrLine error={q.error} style="margin:0 0 12px;" />}
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:0 22px;margin-bottom:12px;')}>
        {steps.map((s, i) => {
          const last = i === steps.length - 1;
          const consent = s.consent ?? null;
          const noC = !consent?.onFile;
          const consentNote = noC
            ? 'Push and in-app only. A critical call skips this number, records why, and goes on to step ' + (s.step + 1) + '.'
            : recorded[s.step]
              ? 'Verbal consent recorded by you just now. Texts and calls both covered; STOP withdraws both.'
              : consent?.source === 'verbal' || consent?.source === 'paper'
                ? (consent.source === 'paper' ? 'Paper' : 'Verbal') + ' consent recorded by ' + (consent.recordedByName ?? 'an admin') + (consent.recordedAt ? ' on ' + new Date(consent.recordedAt).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '') + '. Texts and calls both covered; STOP withdraws both.'
                : 'Opted in at the consent checkbox when the number was added. STOP withdraws both.';
          return (
            <div key={s.step} style={sx('min-width:0;display:flex;flex-direction:column;gap:5px;padding:10px 0;border-top:2px solid #2F5C97;')}>
              <div style={sx('display:flex;align-items:baseline;gap:8px;')}>
                <span style={sx('font-size:20px;font-weight:800;color:#2F5C97;font-variant-numeric:tabular-nums;')}>{I(String(s.step))}</span>
                <span style={sx('font-size:14px;letter-spacing:.09em;text-transform:uppercase;color:#54606C;font-weight:700;')}>{I(s.label)}</span>
              </div>
              <div style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{I(s.name ?? 'Nobody set')}</div>
              <div style={sx('font-size:16px;color:#54606C;')}>{I(s.phone ? maskPhone(s.phone) + (s.phone_verified ? ' · verified' : ' · not verified yet') : 'no phone yet')}</div>
              <div style={sx('display:flex;align-items:center;gap:6px 8px;flex-wrap:wrap;')}>
                <span style={sx('display:inline-flex;align-items:center;height:20px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;' + (noC ? 'color:#8A1C16;background:rgba(179,38,30,.1);' : 'color:#12503C;background:rgba(31,107,82,.12);'))}>
                  {noC ? 'NO TEXT OR CALL CONSENT' : 'TEXTS + CALLS OK'}
                </span>
                {noC && s.user_id && (
                  <a
                    href="#"
                    {...wr(() => void run(async () => {
                      await userContactConsent(client, { params: { id: s.user_id! }, body: { source: 'verbal', wording: CONSENT_WORDS, ...(s.phone ? { phone: s.phone } : {}) } });
                      setRecorded((p) => ({ ...p, [s.step]: true }));
                      q.reload();
                    }))}
                    style={sx('font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}
                  >
                    Record consent
                  </a>
                )}
              </div>
              <div style={sx('font-size:16px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>{I(consentNote)}</div>
              <div style={sx('font-size:16px;color:#3C454F;margin-top:2px;')}>
                {I(last ? 'Last step. Keeps calling every ' + (d?.last_step_repeats_every_min ?? 5) + ' min until someone answers.' : 'No answer? Page step ' + (s.step + 1) + ' after')}
              </div>
              <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;')}>
                {!last && WAITS.map((w) => (
                  <a
                    key={w}
                    href="#"
                    aria-pressed={s.wait_min === w}
                    {...wr(() => void run(async () => { await oncallStepSave(client, { params: { n: String(s.step) }, body: { source: s.source === 'rotation' ? 'rotation' : 'fixed', user_id: s.user_id, wait_min: w, label: s.label } }); q.reload(); }))}
                    style={sx(optStyle(s.wait_min === w))}
                  >
                    {I(w + ' min')}
                  </a>
                ))}
              </div>
              <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:2px;')}>
                <a
                  href="#"
                  {...wr(() => void run(async () => {
                    // No consent on file: no test text (Spec V4 step 3); the server refuses it too.
                    if (noC) {
                      setTested((p) => ({ ...p, [s.step]: 'No text sent: no consent on this number' }));
                      return;
                    }
                    const r = await oncallStepTest(client, { params: { n: String(s.step) }, body: {} });
                    setTested((p) => ({ ...p, [s.step]: r.live ? 'Test text delivered ' + new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'Not sent: ' + (r.reason ?? 'SMS is not set up') }));
                  }))}
                  style={sx('display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:6px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;background:#F7F3EA;color:#22272E;border:1px solid rgba(34,39,46,.2);')}
                >
                  Send test text
                </a>
                {tested[s.step] && (
                  <span style={sx('font-size:16px;font-weight:700;color:#1F6B52;')}>
                    {'✓ '}
                    {I(tested[s.step])}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <ErrLine error={err} style="margin:-6px 0 10px;" />
      <RuleRows rows={rows} pad="9px 0" ro={roReason} />
    </div>
  );
}

// ── Voice & Scribe (design seIsVoice T9943; L22955-22962) ───────────────────

const ASSISTANTS: [VoiceAssistant, string][] = [['siri', 'Siri'], ['google', 'Google'], ['alexa', 'Alexa']];
const NOTES: [ScribeNoteType, string][] = [['resident_note', 'Resident note'], ['shift_handoff', 'Shift handoff'], ['worklist_note', 'Worklist note'], ['incident', 'Incident']];

/** A building's effective value with its waiting requests laid over it (what it asked for). */
function withPending<T extends object>(value: T, pending: { proposed: unknown }[] | undefined): T {
  return (pending ?? []).reduce<T>((v, p) => ({ ...v, ...((p.proposed ?? {}) as Partial<T>) }), value);
}

export function VoiceSection() {
  const { client, session } = useSession();
  const { lens, changed } = usePage();
  const { roReason } = useGuarded();
  const fa = lens.lens === 'Facility Admin';
  const plat = useApi((c, s) => voiceScribeDefault(c, { signal: s }), [], !fa);
  const mine = useApi((c, s) => facilityVoiceScribe(c, { params: { id: 'me' }, signal: s }), [], fa);
  const [err, setErr] = useState<ApiError | null>(null);
  const q = fa ? mine : plat;
  if (!q.data && !q.error) return <Skeleton rows={5} label="Loading Voice and Scribe" />;
  const v: VoiceScribeSettings | null = fa ? (mine.data ? withPending(mine.data.value, mine.data.pending) : null) : plat.data?.value ?? null;
  const pendN = fa ? mine.data?.pendingApproval ?? 0 : 0;
  const save = (patch: Partial<VoiceScribeSettings>) => {
    setErr(null);
    const p = fa ? facilityVoiceScribeSave(client, { params: { id: 'me' }, body: patch }) : voiceScribeDefaultSave(client, { body: patch });
    p.then(() => { q.reload(); changed(); }).catch((e) => setErr(e as ApiError));
  };
  const toggle = <K extends 'assistants' | 'noteTypes'>(key: K, item: VoiceScribeSettings[K][number]) => {
    if (!v) return;
    const cur = v[key] as string[];
    save({ [key]: cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item] } as Partial<VoiceScribeSettings>);
  };
  const rows: RuleRow[] = v ? [
    { label: 'Voice assistants allowed', sub: 'Aithora uses the assistant already on the device. It has no microphone of its own.', opts: ASSISTANTS.map(([k, l]) => ({ label: l, on: v.assistants.includes(k), onClick: () => toggle('assistants', k) })) },
    { label: 'Scribe can draft', sub: 'Atlas listens, drafts, and the staff member signs. Nothing files unsigned.', opts: NOTES.map(([k, l]) => ({ label: l, on: v.noteTypes.includes(k), onClick: () => toggle('noteTypes', k) })) },
    // V5: Scribe keeps no audio (the device's own speech engine sends text only).
    { label: 'Audio', sub: 'No audio is kept: only the transcript is. This stays fixed until the server can accept audio.', opts: [], ...LOCKED_TAG },
    { label: 'Recording consent', sub: 'A resident without consent on file cannot be recorded. Staff-only handoffs are exempt.', opts: [], ...LOCKED_TAG },
  ] : [];
  return (
    <div data-screen-label="Voice and Scribe" data-testid="settings-voice" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 2px;max-width:980px;')}>
      {HEAD('#2F5C97', 'Voice & Scribe')}
      <div style={sx('font-size:16px;color:#54606C;margin-bottom:10px;text-wrap:pretty;')}>
        {I(fa ? 'For ' + (session?.user.orgName ?? 'your building') + ' only. Changes go to Aithora for approval before they apply.' : 'Platform defaults. Each building can make them stricter, with your approval.')}
      </div>
      {pendN > 0 && <div style={sx(PEND)}>{I(pendN + (pendN === 1 ? ' change' : ' changes') + ' sent to Aithora for approval. They apply once approved.')}</div>}
      {q.error && <ErrLine error={q.error} style="margin:0 0 10px;" />}
      <RuleRows rows={rows} ro={roReason} />
      <ErrLine error={err} />
    </div>
  );
}

// ── Guardian rules (design seIsGuardianR T9957; L22963-22974) ───────────────

const OWNERS: [GuardianOwner, string][] = [['charge_nurse', 'Charge nurse'], ['nurse_on_duty', 'Nurse on duty'], ['administrator', 'Administrator']];
const SIGNALS: [GuardianRuleKind, string][] = [['fall', 'Fall'], ['abuse_neglect', 'Suspected abuse or neglect'], ['missing_resident', 'Missing resident'], ['medication_error', 'Medication error']];

export function GuardianRulesSection() {
  const { client, session } = useSession();
  const { lens, changed } = usePage();
  const { roReason } = useGuarded();
  const fa = lens.lens === 'Facility Admin';
  const plat = useApi((c, s) => guardianRulesDefault(c, { signal: s }), [], !fa);
  const mine = useApi((c, s) => facilityGuardianRules(c, { params: { id: 'me' }, signal: s }), [], fa);
  const [err, setErr] = useState<ApiError | null>(null);
  const q = fa ? mine : plat;
  if (!q.data && !q.error) return <Skeleton rows={6} label="Loading Guardian rules" />;
  const v: GuardianRules | null = fa ? (mine.data ? withPending(mine.data.value, mine.data.pending) : null) : plat.data?.value ?? null;
  const pendN = fa ? mine.data?.pendingApproval ?? 0 : 0;
  const save = (patch: Partial<GuardianRules>) => {
    setErr(null);
    const p = fa ? facilityGuardianRulesSave(client, { params: { id: 'me' }, body: patch }) : guardianRulesDefaultSave(client, { body: patch });
    p.then(() => { q.reload(); changed(); }).catch((e) => setErr(e as ApiError));
  };
  const d = v?.deadlinesHours;
  const rows: RuleRow[] = v ? [
    ...SIGNALS.map(([k, l]) => ({ label: l, sub: 'Who owns it when Guardian raises it.', opts: OWNERS.map(([o, ol]) => ({ label: ol, on: v.owners[k] === o, onClick: () => save({ owners: { ...v.owners, [k]: o } }) })) })),
    { label: 'Escalate to Aithora if unclaimed', sub: 'On-call is paged and the building is called.', opts: ([5, 10, 15] as const).map((m) => ({ label: m + ' min', on: v.escalateToAithoraMinutes === m, onClick: () => save({ escalateToAithoraMinutes: m }) })) },
    {
      label: 'Reporting deadlines',
      sub: 'Federal floor: suspected abuse with serious bodily injury within ' + (d?.serious_bodily_injury ?? 2) + ' hours, otherwise within ' + (d?.standard ?? 24) + ' hours. State rules are added on top. A building can make these stricter, never looser.',
      opts: [], ...LOCKED_TAG,
    },
  ] : [];
  return (
    <div data-screen-label="Guardian rules" data-testid="settings-guardian-rules" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 2px;max-width:980px;')}>
      {HEAD('#B3261E', 'Guardian rules · who owns what')}
      <div style={sx('font-size:16px;color:#54606C;margin-bottom:10px;text-wrap:pretty;')}>
        {I(fa ? 'For ' + (session?.user.orgName ?? 'your building') + ' only. Changes go to Aithora for approval before they apply.' : 'Platform defaults. Buildings set their own owners, with your approval.')}
      </div>
      {pendN > 0 && <div style={sx(PEND)}>{I(pendN + (pendN === 1 ? ' change' : ' changes') + ' sent to Aithora for approval. They apply once approved.')}</div>}
      {q.error && <ErrLine error={q.error} style="margin:0 0 10px;" />}
      <RuleRows rows={rows} ro={roReason} />
      <ErrLine error={err} />
      {fa && <FacilityOnCall />}
    </div>
  );
}
